import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import { Helmet } from '@saas-pages/shared/public/seo'
import { seoFor } from '@/seo/routes'
import { useLocale, useT } from '@/shared/i18n'
import { releasePointerFocus, useHotkeys } from '@/shared/lib/hotkeys'
import { HotkeyHints } from '@/shared/ui/HotkeyHints'
import {
  DEFAULT_POMODORO_SETTINGS,
  applyPomodoroSettings,
  createPomodoroState,
  isValidPomodoroSettings,
  loadPomodoroSettings,
  pauseOrCompletePomodoro,
  reconcilePomodoro,
  resetPomodoro,
  savePomodoroSettings,
  startPomodoro,
  type PomodoroPhase,
  type PomodoroSettings,
} from '@/pages/pomodoro/model/pomodoro'

const TICK_MS = 250
const POMODORO_PRESETS = [[25, 5], [50, 10]] as const
const ANALYTICS = {
  app: 'dateify',
  domain: 'dateify.ru',
  route: '/pomodoro-tajmer',
  utility: 'pomodoro-timer',
  processingMode: 'local',
} satisfies ToolAnalyticsContext

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext
}

function createAudioContext(): AudioContext {
  const AudioContextCtor = window.AudioContext ?? (window as AudioWindow).webkitAudioContext
  if (!AudioContextCtor) throw new Error('audio_context_unavailable')
  return new AudioContextCtor()
}

function playCompletionSound(context: AudioContext): void {
  const gain = context.createGain()
  gain.gain.setValueAtTime(0.0001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.8)
  gain.connect(context.destination)

  for (const [index, frequency] of [740, 880, 740].entries()) {
    const oscillator = context.createOscillator()
    oscillator.frequency.setValueAtTime(frequency, context.currentTime)
    oscillator.connect(gain)
    oscillator.start(context.currentTime + index * 0.16)
    oscillator.stop(context.currentTime + index * 0.16 + 0.12)
  }
}

function formatClock(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(totalMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function PomodoroPage() {
  const t = useT()
  const { locale } = useLocale()
  const [settings, setSettings] = useState(DEFAULT_POMODORO_SETTINGS)
  const [timer, setTimer] = useState(() => createPomodoroState(DEFAULT_POMODORO_SETTINGS))
  const [notice, setNotice] = useState<string | null>(null)
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const timerRef = useRef(timer)
  const settingsRef = useRef(settings)
  const audioContextRef = useRef<AudioContext | null>(null)
  const focusOperationRef = useRef<ReturnType<typeof startToolOperation> | null>(null)
  const focusOperationStartedAtRef = useRef<number | null>(null)

  timerRef.current = timer
  settingsRef.current = settings

  useEffect(() => {
    let restored = DEFAULT_POMODORO_SETTINGS
    try {
      restored = loadPomodoroSettings(window.localStorage)
    } catch {
      // Access to the storage object itself can be blocked by browser policy.
    }
    setSettings(restored)
    setTimer(createPomodoroState(restored))
    setSettingsLoaded(true)
  }, [])

  useEffect(() => {
    if (!settingsLoaded) return
    try {
      savePomodoroSettings(window.localStorage, settings)
    } catch {
      // Settings persistence is best-effort and must not affect the timer.
    }
  }, [settings, settingsLoaded])

  useEffect(() => {
    if (timer.status !== 'running') return
    const phase = timer.phase === 'focus' ? t.pomodoro.focus : t.pomodoro.break
    document.title = `${formatClock(timer.remainingMs)} · ${phase} — ${t.pomodoro.title}`
  }, [t.pomodoro.break, t.pomodoro.focus, t.pomodoro.title, timer.phase, timer.remainingMs, timer.status])

  const announceCompletion = useCallback((completedPhase: PomodoroPhase, nowMs: number) => {
    const currentSettings = settingsRef.current
    setNotice(completedPhase === 'focus' ? t.pomodoro.focusDone : t.pomodoro.breakDone)

    if (completedPhase === 'focus' && focusOperationRef.current) {
      const startedAt = focusOperationStartedAtRef.current ?? nowMs
      focusOperationRef.current.success(Math.max(0, nowMs - startedAt))
      focusOperationRef.current = null
      focusOperationStartedAtRef.current = null
    }

    if (currentSettings.sound) {
      try {
        const context = audioContextRef.current ?? createAudioContext()
        audioContextRef.current = context
        playCompletionSound(context)
      } catch {
        // Alerts are best-effort and never change timer completion.
      }
    }

    if (currentSettings.vibration && 'vibrate' in navigator) {
      try {
        navigator.vibrate([180, 100, 180])
      } catch {
        // Best-effort on browsers that expose but restrict vibration.
      }
    }

    if (
      currentSettings.notifications &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      try {
        new Notification(t.pomodoro.notificationTitle, {
          body: completedPhase === 'focus' ? t.pomodoro.focusDone : t.pomodoro.breakDone,
        })
      } catch {
        // Best-effort on browsers that restrict notification construction.
      }
    }
  }, [t.pomodoro.breakDone, t.pomodoro.focusDone, t.pomodoro.notificationTitle])

  const reconcile = useCallback(() => {
    const nowMs = Date.now()
    const result = reconcilePomodoro(timerRef.current, settingsRef.current, nowMs)
    if (result.state !== timerRef.current) {
      timerRef.current = result.state
      setTimer(result.state)
    }
    if (result.completedPhase) announceCompletion(result.completedPhase, nowMs)
  }, [announceCompletion])

  useEffect(() => {
    if (timer.status !== 'running') return
    const id = window.setInterval(reconcile, TICK_MS)
    const handleVisibility = () => {
      if (!document.hidden) reconcile()
    }
    window.addEventListener('focus', reconcile)
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', reconcile)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [reconcile, timer.status])

  useEffect(() => () => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      void audioContextRef.current.close()
    }
    if (focusOperationRef.current) focusOperationRef.current.error('cancelled')
  }, [])

  function updateSettings(patch: Partial<PomodoroSettings>) {
    const nextSettings = { ...settingsRef.current, ...patch }
    settingsRef.current = nextSettings
    setSettings(nextSettings)
    const nextTimer = applyPomodoroSettings(timerRef.current, nextSettings)
    timerRef.current = nextTimer
    setTimer(nextTimer)
    setNotice(null)
  }

  function applyPreset(focusMinutes: number, breakMinutes: number) {
    updateSettings({ focusMinutes, breakMinutes })
  }

  function handleStart() {
    if (!isValidPomodoroSettings(settings)) {
      setNotice(t.pomodoro.invalidDuration)
      trackToolRejection(ANALYTICS, 'invalid_input')
      return
    }

    if (settings.sound) {
      try {
        const context = audioContextRef.current ?? createAudioContext()
        audioContextRef.current = context
        if (context.state === 'suspended') void context.resume()
      } catch {
        audioContextRef.current = null
      }
    }

    const nowMs = Date.now()
    if (timer.phase === 'focus' && !focusOperationRef.current) {
      focusOperationRef.current = startToolOperation(ANALYTICS)
      focusOperationStartedAtRef.current = nowMs
    }
    const nextTimer = startPomodoro(timer, nowMs)
    timerRef.current = nextTimer
    setTimer(nextTimer)
    setNotice(null)
  }

  function handlePause() {
    const nowMs = Date.now()
    const result = pauseOrCompletePomodoro(timerRef.current, settingsRef.current, nowMs)
    timerRef.current = result.state
    setTimer(result.state)
    if (result.completedPhase) announceCompletion(result.completedPhase, nowMs)
  }

  function handleReset() {
    if (focusOperationRef.current) {
      focusOperationRef.current.error('cancelled')
      focusOperationRef.current = null
      focusOperationStartedAtRef.current = null
    }
    const nextTimer = resetPomodoro(settings)
    timerRef.current = nextTimer
    setTimer(nextTimer)
    setNotice(null)
  }

  function handleToggle() {
    if (timer.status === 'running') handlePause()
    else handleStart()
  }

  useHotkeys({ Space: handleToggle, KeyR: handleReset })

  async function handleNotificationAction() {
    if (settingsRef.current.notifications) {
      updateSettings({ notifications: false })
      setNotice(t.pomodoro.notificationsDisabled)
      return
    }

    if (!('Notification' in window)) {
      setNotice(t.pomodoro.notificationsUnavailable)
      return
    }
    const permission = await Notification.requestPermission()
    const enabled = permission === 'granted'
    updateSettings({ notifications: enabled })
    setNotice(enabled ? t.pomodoro.notificationsEnabled : t.pomodoro.notificationsDenied)
  }

  const phaseLabel = timer.phase === 'focus' ? t.pomodoro.focus : t.pomodoro.break
  const statusLabel = t.pomodoro.status[timer.status]
  const settingsDisabled = timer.status === 'running' || timer.status === 'paused'
  const idleTitle = seoFor('pomodoro', locale).title

  return (
    <>
      {timer.status !== 'running' && <Helmet><title>{idleTitle}</title></Helmet>}
      <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">{t.pomodoro.title}</h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.pomodoro.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-rose-400 via-primary to-cyan-400" />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1.1fr)_minmax(310px,0.75fr)]">
          <div className="space-y-3 border-b p-3 sm:space-y-4 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className={cn(
                  'rounded-full px-2.5 py-0.5 text-sm font-semibold',
                  timer.phase === 'focus' ? 'bg-primary text-primary-foreground' : 'bg-cyan-100 text-cyan-950 dark:bg-cyan-900/60 dark:text-cyan-50',
                )}>
                  {phaseLabel}
                </span>
                <span className="rounded-full border px-2.5 py-0.5 text-sm text-muted-foreground">{statusLabel}</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {t.pomodoro.cycles}: <strong className="font-mono text-foreground">{timer.completedFocusCycles}</strong>
              </span>
            </div>

            <div className="rounded-xl border bg-background/70 px-2 py-5 text-center shadow-inner dark:bg-background/80 sm:py-8">
              <div className="font-mono text-6xl font-bold leading-none tabular-nums sm:text-8xl">{formatClock(timer.remainingMs)}</div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2 sm:gap-3">
              <Button
                type="button"
                onClick={(event) => {
                  releasePointerFocus(event)
                  handleToggle()
                }}
                className="h-12 text-base"
              >
                {timer.status === 'running' ? t.pomodoro.pause : timer.status === 'paused' ? t.pomodoro.resume : t.pomodoro.start}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={(event) => {
                  releasePointerFocus(event)
                  handleReset()
                }}
                className="h-12 min-w-24 text-base sm:min-w-28"
              >
                {t.pomodoro.reset}
              </Button>
            </div>

            <HotkeyHints
              label={t.common.hotkeys.label}
              items={[
                { key: t.common.hotkeys.space, action: t.common.hotkeys.startPause },
                { key: 'R', action: t.common.hotkeys.reset },
              ]}
            />

            {notice && <div role="status" className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm">{notice}</div>}

            <p className="text-sm text-muted-foreground">{t.pomodoro.accuracyHint}</p>
          </div>

          <div className="space-y-6 bg-secondary/30 p-5 sm:p-6">
            <div>
              <h2 className="text-lg font-semibold">{t.pomodoro.presets}</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {POMODORO_PRESETS.map(([focus, rest]) => (
                  <button
                    key={focus}
                    type="button"
                    disabled={settingsDisabled}
                    onClick={() => applyPreset(focus, rest)}
                    className={cn('rounded-md border bg-card px-3 py-3 text-left transition-colors hover:border-primary/40 hover:bg-accent', settingsDisabled && 'cursor-not-allowed opacity-60')}
                  >
                    <span className="block font-mono text-lg font-semibold">{focus}/{rest}</span>
                    <span className="text-xs text-muted-foreground">{t.pomodoro.focus}/{t.pomodoro.break}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold">{t.pomodoro.custom}</h2>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <DurationInput id="pomodoro-focus" label={t.pomodoro.focusMinutes} value={settings.focusMinutes} max={180} disabled={settingsDisabled} onChange={(focusMinutes) => updateSettings({ focusMinutes })} />
                <DurationInput id="pomodoro-break" label={t.pomodoro.breakMinutes} value={settings.breakMinutes} max={60} disabled={settingsDisabled} onChange={(breakMinutes) => updateSettings({ breakMinutes })} />
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold">{t.pomodoro.alerts}</h2>
              <div className="mt-3 space-y-2">
                <AlertToggle label={t.pomodoro.sound} checked={settings.sound} disabled={settingsDisabled} onChange={(sound) => updateSettings({ sound })} />
                <AlertToggle label={t.pomodoro.vibration} checked={settings.vibration} disabled={settingsDisabled} onChange={(vibration) => updateSettings({ vibration })} />
                <Button type="button" variant="outline" disabled={settingsDisabled} onClick={() => void handleNotificationAction()} className="h-auto w-full justify-start whitespace-normal py-2.5 text-left">
                  {settings.notifications ? t.pomodoro.disableNotifications : t.pomodoro.enableNotifications}
                </Button>
              </div>
              <p className="mt-3 text-xs leading-5 text-muted-foreground">{t.pomodoro.alertsHint}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="mt-10 grid gap-8 border-t pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.7fr)]">
        <div>
          <h2 className="text-xl font-semibold">{t.pomodoro.faqTitle}</h2>
          <dl className="mt-4 space-y-4">
            {t.pomodoro.faq.map((item) => <div key={item.question}><dt className="font-medium">{item.question}</dt><dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd></div>)}
          </dl>
        </div>
        <aside>
          <h2 className="text-xl font-semibold">{t.pomodoro.relatedTitle}</h2>
          <div className="mt-4 space-y-2">
            {t.pomodoro.related.map((item) => <a key={item.href} href={item.href} className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent"><span className="text-sm font-medium">{item.title}</span><span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.description}</span></a>)}
          </div>
        </aside>
      </section>
      </div>
    </>
  )
}

function DurationInput({ id, label, value, max, disabled, onChange }: { id: string; label: string; value: number; max: number; disabled: boolean; onChange: (value: number) => void }) {
  return <label htmlFor={id} className="block"><span className="mb-2 block text-sm font-medium">{label}</span><input id={id} type="number" min={1} max={max} step={1} inputMode="numeric" value={value} disabled={disabled} onChange={(event) => onChange(Number(event.target.value))} className="h-11 w-full rounded-md border border-input bg-card px-3 font-mono text-lg shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60" /></label>
}

function AlertToggle({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled: boolean; onChange: (checked: boolean) => void }) {
  return <label className={cn('flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-sm', disabled && 'opacity-60')}><span>{label}</span><input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-primary" /></label>
}
